import axios from "axios";
import { API_URL } from "./env";
import { clearAuthSession, getAccessToken, setAuthSession } from "./authSession";

export const BASE_URL = API_URL;

// Клиент для auth-эндпоинтов (refresh, login, logout)
export const AuthClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // обязательно, чтобы куки ходили
});

// Клиент для ресурсных запросов
export const ResourceClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // не мешает; куки пригодятся при CORS и т.п.
});

// Добавляем accessToken из localStorage во все ресурсные запросы
ResourceClient.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Авто-рефреш токена на 401:
 * - один одновременный refresh
 * - остальные запросы ждут, потом ретраим с новым токеном
 */
let isRefreshing = false;
let queue = [];

function subscribeTokenRefresh(cb) {
  queue.push(cb);
}
function onRefreshed(newToken) {
  queue.forEach((cb) => cb(newToken));
  queue = [];
}

ResourceClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    // если не 401 или уже пробовали рефреш — просто отдать ошибку
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    original._retry = true;

    // Если уже идёт общий refresh — ждём его
    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((newToken) => {
          if (newToken) {
            original.headers.Authorization = `Bearer ${newToken}`;
          } else {
            delete original.headers.Authorization;
          }
          resolve(ResourceClient(original));
        });
      });
    }

    // Иначе инициируем новый refresh
    try {
      isRefreshing = true;
      const r = await AuthClient.post("/refresh", null, { withCredentials: true });
      const newToken = r.data?.accessToken;
      setAuthSession({
        accessToken: newToken,
        accessTokenExpiration: r.data?.accessTokenExpiration,
      });
      onRefreshed(newToken);
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
      } else {
        delete original.headers.Authorization;
      }
      return ResourceClient(original);
    } catch (e) {
      clearAuthSession();
      onRefreshed(null);
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

// Вспомогательные API для админки (без изменений)
export const UsersApi = {
  getUnits: () => ResourceClient.get("/unit"),
  getLessons: () => ResourceClient.get("/lesson"),
  toggleLessonAccess: (lessonId, body) =>
    ResourceClient.patch(`/lesson/${lessonId}/access`, body),
  getAll: () => ResourceClient.get("/users"),
  getById: (id) => ResourceClient.get(`/users/${id}`),
  update: (id, data) => ResourceClient.put(`/users/${id}`, data),
  remove: (id) => ResourceClient.delete(`/users/${id}`),
};

export const HomeworkApi = {
  getAll: () => ResourceClient.get("/homework"),
  getByLessonAndUser: (lessonId, userId) =>
    ResourceClient.get(`/homework/lesson/${lessonId}/user/${userId}`),
  review: (lessonId, userId, { status, feedback, score }) =>
    ResourceClient.patch(`/homework/admin/${lessonId}/user/${userId}`, {
      status,
      feedback,
      score,
    }),
};

export const LessonApi = {
  getAll: () => ResourceClient.get("/lesson"),
  getById: (lessonId) => ResourceClient.get(`/lesson/${lessonId}`),
};
